import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

export default function TeslimatIadePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">QuizMind</h1>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100 shadow-sm"
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Teslimat ve İade Şartları
          </h1>
          <p className="text-sm text-slate-500 mb-8">Son Güncelleme: Şubat 2026</p>

          <div className="space-y-10 text-slate-700 leading-relaxed">

            {/* Bölüm 1 */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                1. Ürün Hakkında
              </h2>
              <p>
                QuizMind, kullanıcılarına kişilik testi ve öz-değerlendirme raporları sunan bir dijital platform
                hizmetidir. Satışa konu olan tüm ürünler, fiziksel bir teslimat gerektirmeyen <strong>dijital
                içerik</strong> niteliğindedir. Satın alma işlemi tamamlandığı anda içerik kullanıcıya anında
                sunulmaktadır.
              </p>
            </section>

            {/* Bölüm 2 */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                2. Teslimat Şartları
              </h2>
              <p className="mb-3">
                Satın alınan dijital içerik aşağıdaki yöntemlerle teslim edilmektedir:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold mt-0.5">•</span>
                  <span><strong>Anlık Ekran Erişimi:</strong> Ödemenin onaylanmasının hemen ardından test sonuç raporu ekranda görüntülenir.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold mt-0.5">•</span>
                  <span><strong>E-posta ile Teslimat:</strong> Alıcı'nın satın alma sırasında belirttiği e-posta adresine sonuç raporu gönderilir.</span>
                </li>
              </ul>
              <p className="mt-4 text-sm bg-violet-50 border border-violet-100 rounded-2xl px-4 py-3 text-violet-800">
                Teslimat süresi ödeme onayının ardından <strong>anında</strong> gerçekleşir. Herhangi bir gecikme
                yaşanması durumunda support@quizmind.net adresinden bizimle iletişime geçebilirsiniz.
              </p>
            </section>

            {/* Bölüm 3 */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                3. İade ve Cayma Hakkı
              </h2>
              <div className="bg-violet-50 border border-violet-100 rounded-2xl px-4 py-3 mb-4">
                <p className="font-semibold text-violet-800 text-sm">
                  Hizmetimizden memnun kalmadıysanız, test sonuç sayfanızın bağlantısını (linkini){' '}
                  <a href="mailto:iade@quizmind.net" className="underline hover:text-violet-600">iade@quizmind.net</a>{' '}
                  adresine ileterek ücret iadesi talebinde bulunabilirsiniz.
                </p>
              </div>
              <p>
                Tüm iade talepleri bireysel olarak değerlendirilerek en kısa sürede yanıtlanacaktır.
              </p>
            </section>

            {/* Bölüm 4 */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                4. İstisna Durumlar
              </h2>
              <p className="mb-3">
                Yalnızca aşağıdaki durumlarda satıcı inisiyatifinde değerlendirme yapılabilir:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold mt-0.5">•</span>
                  <span>Ödeme başarıyla alındığı hâlde içeriğe erişim hiç sağlanamamışsa</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold mt-0.5">•</span>
                  <span>Aynı sipariş için mükerrer ücretlendirme yapılmışsa</span>
                </li>
              </ul>
              <p className="mt-3 text-sm text-slate-500">
                Bu durumlarda 48 saat içinde support@quizmind.net adresine ödeme kanıtıyla birlikte başvurmanız
                gerekmektedir. Talepler incelenerek sonuçlandırılacaktır.
              </p>
            </section>

            {/* Bölüm 5 */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                5. İletişim
              </h2>
              <p>
                Teslimat veya ödemeye ilişkin her türlü soru ve sorun için bizimle iletişime geçebilirsiniz:
              </p>
              <p className="mt-2 font-medium text-slate-900">
                Destek: <a href="mailto:support@quizmind.net" className="text-violet-600 hover:underline">support@quizmind.net</a>
              </p>
              <p className="mt-1 font-medium text-slate-900">
                İade Talepleri: <a href="mailto:iade@quizmind.net" className="text-violet-600 hover:underline">iade@quizmind.net</a>
              </p>
            </section>

          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-sm border-t border-slate-100 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-slate-600">QuizMind</span>
            </div>
            <p className="text-sm text-slate-600">QuizMind © 2026</p>
          </div>
        </div>
      </footer>
    </div>
  );
}